// Mirrors backend com.kervolt.oms.corporatetax.dto records 1:1 (wave 3, lane G).

export type TaxpayerCategory =
    'LISTED_COMPANY' | 'NON_LISTED_COMPANY' | 'SMALL_COMPANY' | 'BANK_FINANCIAL_INSTITUTION' | 'RMG_EXPORTER' | 'OTHER';
export type CreditCategory =
    'IMPORT_AIT' | 'EXPORT_SOURCE_TAX' | 'CASH_INCENTIVE_AIT' | 'CUSTOMER_TDS' | 'BANK_INTEREST_AIT'
    | 'ADVANCE_TAX_PAID' | 'SET_OFF' | 'OTHER';
export type CreditStatus = 'OPEN' | 'SET_OFF';
export type TaxDocStatus = 'DRAFT' | 'POSTED' | 'REVERSED';
export type TaxPaymentKind = 'ADVANCE_INSTALLMENT' | 'RETURN_PAYMENT' | 'OTHER';
export type AdjustmentNature = 'PERMANENT' | 'TEMPORARY';
export type AdjustmentSource = 'MANUAL' | 'DEPRECIATION';
export type ChargeBasis = 'REGULAR' | 'MINIMUM' | 'FINAL_SOURCE';

export const TAXPAYER_CATEGORIES: TaxpayerCategory[] =
    ['LISTED_COMPANY', 'NON_LISTED_COMPANY', 'SMALL_COMPANY', 'BANK_FINANCIAL_INSTITUTION', 'RMG_EXPORTER', 'OTHER'];
export const CREDIT_CATEGORIES: CreditCategory[] =
    ['IMPORT_AIT', 'EXPORT_SOURCE_TAX', 'CASH_INCENTIVE_AIT', 'CUSTOMER_TDS', 'BANK_INTEREST_AIT', 'ADVANCE_TAX_PAID', 'SET_OFF', 'OTHER'];

export interface AitAccount { accountId: string; code: string; name: string }

export interface CorporateTaxSettings {
    institutionId: string;
    taxpayerCategory?: TaxpayerCategory | null;
    currentTaxExpenseAccountId?: string | null;
    incomeTaxPayableAccountId?: string | null;
    deferredTaxExpenseAccountId?: string | null;
    deferredTaxLiabilityAccountId?: string | null;
    deferredTaxAssetAccountId?: string | null;
    aitAccounts: AitAccount[];
    fiscalYearStartMonth: number;
}

export interface CorporateTaxSettingsRequest {
    taxpayerCategory?: TaxpayerCategory | null;
    currentTaxExpenseAccountId?: string | null;
    incomeTaxPayableAccountId?: string | null;
    deferredTaxExpenseAccountId?: string | null;
    deferredTaxLiabilityAccountId?: string | null;
    deferredTaxAssetAccountId?: string | null;
    aitAccountIds: string[];
}

export interface SuggestedAccountsResult { created: string[]; existing: string[] }

export interface CorporateTaxRate {
    id: string;
    institutionId: string;
    incomeYear: number;
    category: TaxpayerCategory;
    regularRatePct: number;
    minimumTaxRatePct: number;
    exportSourceTaxFinal: boolean;
    origin: 'MANUAL' | 'DEFAULT';
    note?: string | null;
}

export interface CorporateTaxRateRequest {
    incomeYear: number;
    category: TaxpayerCategory;
    regularRatePct: number;
    minimumTaxRatePct: number;
    exportSourceTaxFinal: boolean;
    note?: string | null;
}

export interface LoadDefaultsResult { added: number; skipped: number }

export interface LedgerRow {
    lineId: string;
    entryId: string;
    entryNumber: string;
    date: string;
    referenceType?: string | null;
    referenceId?: string | null;
    description?: string | null;
    accountId: string;
    accountCode?: string | null;
    accountName?: string | null;
    category: CreditCategory;
    /** Signed: a credit earned is positive, a set-off negative. */
    amount: number;
    segmentId?: string | null;
    certificateNumber?: string | null;
    certificateDate?: string | null;
    issuer?: string | null;
    notes?: string | null;
    status?: CreditStatus | null;
}

export interface LedgerTotal { category: CreditCategory; lineCount: number; total: number; open: number; setOff: number }

export interface TaxCreditLedger {
    institutionId: string;
    incomeYear: number;
    fromDate: string;
    toDate: string;
    rows: LedgerRow[];
    totals: LedgerTotal[];
    unmappedCount: number;
    openTotal: number;
    setOffTotal: number;
    accounts: AitAccount[];
}

export interface EvidenceRequest {
    certificateNumber?: string | null;
    certificateDate?: string | null;
    issuer?: string | null;
    notes?: string | null;
}

export interface AdjustmentInput { label: string; nature: AdjustmentNature; amount: number }
export interface TaxAdjustment extends AdjustmentInput { source: AdjustmentSource }

export interface ComputationRequest {
    fiscalYearStart: string;
    taxpayerCategory?: TaxpayerCategory | null;
    adjustments: AdjustmentInput[];
    includeDepreciationAdjustment: boolean;
    notes?: string | null;
}

export interface TaxComputation {
    id?: string | null;
    institutionId: string;
    fiscalYearStart: string;
    fiscalYearEnd: string;
    status: TaxDocStatus;
    taxpayerCategory: TaxpayerCategory;
    recordedNetProfit: number;
    taxExpenseAddBack: number;
    profitBeforeTax: number;
    adjustments: TaxAdjustment[];
    adjustmentsTotal: number;
    taxableIncome: number;
    regularRatePct: number;
    regularTax: number;
    grossReceipts: number;
    minimumTaxRatePct: number;
    minimumTax: number;
    exportSourceTaxFinal: boolean;
    finalSourceTax: number;
    taxCharge: number;
    chargeBasis?: ChargeBasis | null;
    notes?: string | null;
    journalEntryId?: string | null;
    reversalJournalEntryId?: string | null;
    postedAt?: string | null;
    reversedAt?: string | null;
    warnings: string[];
}

export interface DeferredRequest { fiscalYearStart: string; taxWdv: number; ratePct?: number | null; notes?: string | null }

export interface DeferredTax {
    id?: string | null;
    institutionId: string;
    fiscalYearStart: string;
    fiscalYearEnd: string;
    status: TaxDocStatus;
    bookNbv: number;
    taxWdv: number;
    ratePct: number;
    temporaryDifference: number;
    /** Signed: positive = liability, negative = asset. */
    openingBalance: number;
    closingBalance: number;
    movement: number;
    notes?: string | null;
    journalEntryId?: string | null;
    reversalJournalEntryId?: string | null;
    postedAt?: string | null;
    reversedAt?: string | null;
    warnings: string[];
}

export interface TaxPaymentRequest {
    kind: TaxPaymentKind;
    paymentDate: string;
    amount: number;
    bankGlAccountId: string;
    aitAccountId?: string | null;
    challanNumber: string;
    challanDate: string;
    fiscalYearStart?: string | null;
    notes?: string | null;
}

export interface TaxPayment {
    id: string;
    institutionId: string;
    kind: TaxPaymentKind;
    paymentDate: string;
    amount: number;
    bankGlAccountId: string;
    aitAccountId?: string | null;
    challanNumber: string;
    challanDate: string;
    fiscalYearStart?: string | null;
    notes?: string | null;
    journalEntryId?: string | null;
    reversalJournalEntryId?: string | null;
    reversedAt?: string | null;
}

export interface SetOffRequest { computationId: string; setOffDate: string; lineIds: string[]; notes?: string | null }

export interface SetOff {
    id: string;
    institutionId: string;
    computationId: string;
    setOffDate: string;
    amount: number;
    notes?: string | null;
    journalEntryId?: string | null;
    reversalJournalEntryId?: string | null;
    reversedAt?: string | null;
    lineIds: string[];
}
